import React, { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { resolveWorkshopEntryFromRaw } from '../../../services/lotScanResolve';

/**
 * Ouverture rapide d’un lot (bureau PC) — saisie n° lot / cas, sans caméra.
 * Toutes les commandes sont ouvrables (plus de blocage « non assigné »).
 */
const ProductionScanHero = ({ visibilityCtx: _visibilityCtx, knownBatches: _knownBatches = [] }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [lotCode, setLotCode] = useState('');
  const [opening, setOpening] = useState(false);

  const openBatch = useCallback(
    async (raw) => {
      const trimmed = String(raw ?? '').trim();
      if (!trimmed) return;

      setOpening(true);
      try {
        const { batch, error, info } = await resolveWorkshopEntryFromRaw(trimmed);
        if (!batch?.id) {
          toast.error(error || t('productionTracking.lotSearch.notFound'));
          inputRef.current?.focus();
          return;
        }

        if (info) toast.info(info);
        toast.success(t('productionTracking.lotSearch.opening'));
        navigate(`/atelier-lot?batch=${encodeURIComponent(batch.id)}`);
      } catch (e) {
        console.error(e);
        toast.error(t('productionTracking.lotSearch.error'));
        inputRef.current?.focus();
      } finally {
        setOpening(false);
      }
    },
    [navigate, t]
  );

  const handleSubmit = (e) => {
    e?.preventDefault();
    openBatch(lotCode);
  };

  return (
    <section className="rounded-xl border border-border bg-card p-4 md:p-5 mb-4 md:mb-6">
      <div className="mb-3">
        <h2 className="text-lg font-semibold text-foreground flex items-center gap-2 tracking-tight">
          <Icon name="Search" size={20} className="text-primary" aria-hidden />
          {t('productionTracking.lotSearch.title')}
        </h2>
        <p className="text-sm text-muted-foreground mt-0.5 leading-relaxed">
          {t('productionTracking.lotSearch.subtitle')}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2 sm:gap-3">
        <div className="relative flex-1 min-w-0">
          <label htmlFor="tracking-lot-code" className="sr-only">
            {t('productionTracking.lotSearch.placeholder')}
          </label>
          <Icon
            name="Package"
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
            aria-hidden
          />
          <input
            ref={inputRef}
            id="tracking-lot-code"
            type="text"
            value={lotCode}
            onChange={(e) => setLotCode(e.target.value)}
            placeholder={t('productionTracking.lotSearch.placeholder')}
            disabled={opening}
            autoFocus
            className="w-full min-h-[44px] pl-10 pr-4 rounded-lg border border-input bg-background text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 transition-colors duration-200"
            autoComplete="off"
          />
        </div>
        <Button
          type="submit"
          className="min-h-[44px] px-6 font-semibold shrink-0 cursor-pointer"
          loading={opening}
          disabled={opening || !lotCode.trim()}
          iconName="Factory"
          iconPosition="left"
        >
          {t('productionTracking.lotSearch.open')}
        </Button>
      </form>
    </section>
  );
};

export default ProductionScanHero;
